import { api } from '@/services/api/AxiosBaseService'
import type { SingleResponse } from '@/types/api'

export type MedicarRunStatus = 'EM_ANDAMENTO' | 'CONCLUIDO' | 'FALHOU'
export type MedicarRequestStatus = 'SUCCESS' | 'ERROR' | 'SKIPPED'
export type MedicarOperation = 'CREATE' | 'EDIT' | 'UPDATE' | 'BLOCK' | 'UNBLOCK' | 'LOOKUP'
/** FULL = rodada completa · TEST = um associado · CHECK = consulta de protocolos (só leitura) · RESEND = reenvio forçado */
export type MedicarRunKind = 'FULL' | 'TEST' | 'CHECK' | 'RESEND'

export interface MedicarSyncRequest {
  id: string
  subscriberId: string | null
  subscriberName: string | null
  subscriberCpf: string | null
  operation: MedicarOperation
  endpoint: string | null
  status: MedicarRequestStatus
  httpStatus: number | null
  errorMessage: string | null
  /** JSON já formatado (string) — preserva as chaves originais da Medicar (BBA_*, B2N_*). */
  requestPayload: string | null
  responseBody: string | null
  createdAt: string | null
}

export interface MedicarSyncRunSummary {
  id: string
  kind: MedicarRunKind
  status: MedicarRunStatus
  fake: boolean
  userName: string | null
  subscriberId: string | null
  eligibleCount: number
  skippedCount: number
  totalCount: number
  successCount: number
  errorCount: number
  createdCount: number
  updatedCount: number
  blockedCount: number
  startedAt: string | null
  finishedAt: string | null
  errorMessage: string | null
  createdAt: string | null
}

export interface MedicarSyncRun extends MedicarSyncRunSummary {
  requests: MedicarSyncRequest[]
}

export interface MedicarOverview {
  configured: boolean
  fake: boolean
  missing: string[]
  /** Titulares + dependentes na regra — o que a rodada movimenta. */
  livesCount: number
  holdersCount: number
  dependentsCount: number
  linkedPlans: number
  syncedCount: number
  toBlockCount: number
  /** Protocolo enviado e ainda sem matrícula — alvo de "Consultar todos". */
  awaitingCount: number
  latestRun: MedicarSyncRunSummary | null
  scheduleText: string
}

export interface MedicarDay {
  day: string
  runs: number
  failed: boolean
  errors: boolean
}

export interface MedicarEligible {
  id: string
  name: string
  cpf: string | null
  plan: string | null
  medicarId: string | null
  syncedAt: string | null
  dependents: number
  /** Motivo pelo qual o cadastro não passaria na Medicar (null = completo). */
  incomplete: string | null
}

/** O que a próxima sincronização faria com cada vida. */
export type MedicarPreviewAction = 'CREATE' | 'AWAITING' | 'UPDATE' | 'UNCHANGED' | 'BLOCK' | 'UNBLOCK' | 'MANUAL' | 'INCOMPLETE'

export interface MedicarPreviewRow {
  id: string
  name: string
  cpf: string | null
  type: 'TITULAR' | 'DEPENDENTE'
  /** Nome do titular quando a linha é de dependente. */
  holderName: string | null
  plan: string | null
  medicarId: string | null
  syncedAt: string | null
  action: MedicarPreviewAction
  /** Frase pronta explicando por que a vida caiu nessa situação. */
  reason: string
}

export interface MedicarPreviewSummary {
  /** Todas as linhas da prévia, inclusive as exclusões. */
  analyzed: number
  /** Vidas na regra — o mesmo número que a tela mostra (não inclui exclusões). */
  inRule: number
  holders: number
  dependents: number
  create: number
  /** Protocolo em análise na Medicar (ou dependente esperando o titular). */
  awaiting: number
  update: number
  unchanged: number
  block: number
  unblock: number
  /** Dependente inativado com titular ativo: exclusão manual na Medicar. */
  manual: number
  incomplete: number
  /** create + update + block + unblock — as que geram requisição de fato. */
  sending: number
}

export interface MedicarPreview {
  rows: MedicarPreviewRow[]
  summary: MedicarPreviewSummary
  /** Quantas linhas passaram no filtro (pode ser maior que `rows`). */
  filtered: number
  capped: boolean
  cap: number
  /** Quando a conta foi feita — o resultado é reaproveitado por alguns minutos. */
  computedAt: string
}

/**
 * Retrato cru de uma chamada HTTP à Medicar — é o que se manda para o time
 * deles quando a resposta não faz sentido. A senha vem mascarada.
 */
export interface MedicarExchange {
  method: string
  url: string
  requestHeaders: Record<string, string>
  requestQuery?: Record<string, string>
  requestBody: unknown
  httpStatus: number | null
  responseHeaders: Record<string, string>
  responseBody: string | null
  durationMs: number | null
  error: string | null
}

export interface MedicarAuthTest {
  auth: {
    ok: boolean
    endpoint: string
    error: string | null
    tokenHint: string | null
    exchange: MedicarExchange | null
  }
  contract: { ok: boolean; error: string | null; response: unknown; exchange: MedicarExchange | null; warning?: string | null } | null
  fake: boolean
}

export interface MedicarDryRun {
  dryRun: true
  auth: MedicarAuthTest['auth']
  operation: 'CREATE' | 'EDIT' | 'UPDATE'
  payload: string
}

/** Uma vida (titular ou dependente) e a situação dela na Medicar. */
export interface MedicarPerson {
  id: string
  name: string
  cpf: string | null
  type: 'TITULAR' | 'DEPENDENTE'
  status: string
  holderName: string | null
  medicarId: string | null
  protocolAt: string | null
  protocolNumber: string | null
  protocolStatus: string | null
  protocolLabel: string | null
  checkedAt: string | null
}

const BASE = '/integrations/medicar'

export const MedicarService = {
  async overview(): Promise<MedicarOverview> {
    const r = await api.get<SingleResponse<MedicarOverview>>(`${BASE}/overview`)
    return r.data.data
  },

  /** Dias do mês (YYYY-MM) que têm rodada — para marcar no calendário. */
  async days(month: string): Promise<MedicarDay[]> {
    const r = await api.get<{ data: MedicarDay[] }>(`${BASE}/sync-days`, { params: { month } })
    return r.data.data
  },

  /** Rodadas de um dia (YYYY-MM-DD), mais recente primeiro. */
  async runsOn(date: string): Promise<MedicarSyncRunSummary[]> {
    const r = await api.get<{ data: MedicarSyncRunSummary[] }>(`${BASE}/sync-runs`, { params: { date } })
    return r.data.data
  },

  async run(id: string): Promise<MedicarSyncRun> {
    const r = await api.get<SingleResponse<MedicarSyncRun>>(`${BASE}/sync-runs/${id}`)
    return r.data.data
  },

  /** Associados na regra (ATIVOS em plano com Medicar), em ordem alfabética. */
  async eligible(q?: string): Promise<{ data: MedicarEligible[]; total: number; linkedPlans: number }> {
    const r = await api.get<{ data: MedicarEligible[]; meta: { total: number; linkedPlans: number } }>(
      `${BASE}/eligible`, { params: { q: q || undefined } },
    )
    return { data: r.data.data, total: r.data.meta.total, linkedPlans: r.data.meta.linkedPlans }
  },

  /**
   * O que a próxima sincronização faria — nada é enviado à Medicar.
   * Os números do topo valem para a base inteira; a lista vem limitada.
   */
  async preview(q?: string, action?: MedicarPreviewAction | '', fresh = false): Promise<MedicarPreview> {
    const r = await api.get<{
      data: MedicarPreviewRow[]
      meta: { summary: MedicarPreviewSummary; filtered: number; capped: boolean; cap: number; computedAt: string }
    }>(`${BASE}/preview`, { params: { q: q || undefined, action: action || undefined, fresh: fresh ? 1 : undefined } })
    return { rows: r.data.data, ...r.data.meta }
  },

  async sync(): Promise<MedicarSyncRunSummary> {
    const r = await api.post<{ data: MedicarSyncRunSummary }>(`${BASE}/sync`)
    return r.data.data
  },

  async testAuth(): Promise<MedicarAuthTest> {
    const r = await api.post<SingleResponse<MedicarAuthTest>>(`${BASE}/test-auth`)
    return r.data.data
  },

  /** Envia um associado. Com `dryRun`, só monta o payload e autentica. */
  async test(subscriberId: string, dryRun: boolean): Promise<MedicarSyncRun | MedicarDryRun> {
    const r = await api.post<SingleResponse<MedicarSyncRun | MedicarDryRun>>(`${BASE}/test`, {
      subscriber_id: subscriberId,
      dry_run: dryRun,
    })
    return r.data.data
  },

  /** Vidas para a consulta de protocolos. Sem busca: quem espera há mais tempo. */
  async people(q?: string): Promise<MedicarPerson[]> {
    const r = await api.get<{ data: MedicarPerson[] }>(`${BASE}/people`, { params: { q: q || undefined } })
    return r.data.data
  },

  /** Consulta na hora o protocolo/matrícula das vidas escolhidas (até 20). Só leitura. */
  async check(subscriberIds: string[]): Promise<MedicarSyncRun> {
    const r = await api.post<SingleResponse<MedicarSyncRun>>(`${BASE}/check`, { subscriber_ids: subscriberIds })
    return r.data.data
  },

  /** Consulta todos os aguardando análise, em segundo plano. */
  async checkAwaiting(): Promise<MedicarSyncRunSummary> {
    const r = await api.post<{ data: MedicarSyncRunSummary }>(`${BASE}/check-awaiting`)
    return r.data.data
  },

  /** Reenvia a inclusão de uma vida, mesmo com protocolo em análise. */
  async resend(subscriberId: string): Promise<MedicarSyncRun> {
    const r = await api.post<SingleResponse<MedicarSyncRun>>(`${BASE}/resend`, { subscriber_id: subscriberId })
    return r.data.data
  },

  async deleteRun(id: string): Promise<void> {
    await api.delete(`${BASE}/sync-runs/${id}`)
  },
}

/** Configurações → Integrações → Medicar. A senha nunca volta em claro. */
export interface MedicarSettings {
  endpoint: string | null
  username: string | null
  tenantId: string | null
  cnpjMedicar: string | null
  codInt: string | null
  codEmp: string | null
  conEmp: string | null
  verCon: string | null
  subCon: string | null
  verSub: string | null
  codPro: string | null
  versao: string | null
  grupoEmpresa: string | null
  contrato: string | null
  blockReason: string | null
  /** Endereço da Ampla+ que vai em todo beneficiário (o Protheus valida o CEP). */
  addressPostalCode: string | null
  addressStreet: string | null
  addressNumber: string | null
  addressComplement: string | null
  addressDistrict: string | null
  addressCity: string | null
  addressState: string | null
  /** Ritmo da integração: tetos e intervalo entre conferências. */
  reconcileLimit: number
  reconcileIntervalHours: number
  syncMaxRequests: number
  fromEnv: { endpoint: string | null; username: string | null }
  password: { filled: boolean; hint: string | null; fromEnv: boolean }
  complete: boolean
  /** Número do contrato diferente do subcontrato — a consulta de matrícula não acha ninguém. */
  contractWarning: string | null
  missing: string[]
  linkedPlans: number
  statusText: string
}

export type MedicarSettingsPayload = Partial<Omit<MedicarSettings, 'fromEnv' | 'password' | 'complete' | 'missing' | 'linkedPlans' | 'statusText'
  | 'reconcileLimit' | 'reconcileIntervalHours' | 'syncMaxRequests'>> & {
  password?: string | null
  /** Ritmo: null limpa o campo e devolve o valor padrão. */
  reconcileLimit?: number | null
  reconcileIntervalHours?: number | null
  syncMaxRequests?: number | null
}

const SETTINGS = '/settings/integrations/medicar'

export const MedicarSettingsService = {
  async get(): Promise<MedicarSettings> {
    const r = await api.get<{ data: MedicarSettings }>(SETTINGS)
    return r.data.data
  },
  async update(payload: MedicarSettingsPayload): Promise<MedicarSettings> {
    const r = await api.put<{ data: MedicarSettings }>(SETTINGS, payload)
    return r.data.data
  },
  async clearPassword(): Promise<MedicarSettings> {
    const r = await api.delete<{ data: MedicarSettings }>(`${SETTINGS}/password`)
    return r.data.data
  },
}
